import { useState, useEffect, useCallback } from "react";
import { FileSignature, Plus, CheckCircle2, Circle } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { PageHeader, Loading, Empty, Pill, SectionCard, RecordMeta } from "../components/kit";
import { Button } from "../components/ui/button";
import { FormModal, Field, Input, Select } from "../components/form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { money, titleCase } from "../lib/constants";
import { toast } from "sonner";

const STATUSES = ["pre_contract", "under_contract", "pending", "closing", "closed", "cancelled"];
const DEFAULT_CHECKLIST = ["Contract signed", "Earnest money", "Inspection", "Inspection response", "Appraisal", "Loan approval", "Title", "Insurance", "Final walkthrough", "Closing documents", "Closing"];

export default function Transactions() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/transactions?limit=500"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-up">
      <PageHeader title="Transactions" subtitle="Track every deal from contract to closing with milestone checklists." testid="transactions-header"
        actions={<Button className="bg-navy-800 hover:bg-navy-700 text-white" onClick={() => setModal(true)} data-testid="new-transaction-btn"><Plus className="w-4 h-4 mr-1.5" />New Transaction</Button>} />
      {!items ? <Loading /> : items.length === 0 ? <Empty title="No transactions" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="transactions-grid">
          {items.map((t) => {
            const done = (t.checklist || []).filter((c) => c.status === "done").length;
            const total = (t.checklist || []).length || 1;
            return (
              <div key={t.id} onClick={() => setDetail(t)} data-testid={`transaction-card-${t.id}`} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-gold/40 cursor-pointer transition-[box-shadow,border-color]">
                <div className="flex items-start justify-between">
                  <div><p className="font-display font-semibold text-navy-800">{t.property_address || "Transaction"}</p><p className="text-xs text-slate-400">{t.buyer_name} → {t.seller_name}</p></div>
                  <Pill>{titleCase(t.status)}</Pill>
                </div>
                <p className="font-display text-2xl font-bold text-navy-800 mt-3">{money(t.purchase_price)}</p>
                <div className="mt-3"><div className="flex justify-between text-xs text-slate-400 mb-1"><span>Milestones</span><span>{done}/{total}</span></div>
                  <div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-gold to-gold-dark" style={{ width: `${(done / total) * 100}%` }} /></div></div>
                {t.closing_date && <p className="text-xs text-slate-400 mt-3">Closing: {t.closing_date}</p>}
                <div className="mt-3 pt-3 border-t border-slate-100"><RecordMeta doc={t} testid={`txn-meta-${t.id}`} /></div>
              </div>
            );
          })}
        </div>
      )}
      <NewTransactionModal open={modal} onClose={() => { setModal(false); load(); }} onSaved={load} />
      {detail && <TransactionDetail txn={detail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function NewTransactionModal({ open, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setF({ status: "under_contract" }); }, [open]);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/transactions", { ...f, checklist: DEFAULT_CHECKLIST.map((item) => ({ item, status: "pending" })) });
      toast.success("Transaction created"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } setLoading(false);
  };
  return (
    <FormModal open={open} onClose={onClose} title="New Transaction" onSubmit={submit} loading={loading} testid="transaction-modal">
      <Field label="Property address" required><Input value={f.property_address || ""} onChange={(e) => setF({ ...f, property_address: e.target.value })} required data-testid="txn-address" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Buyer"><Input value={f.buyer_name || ""} onChange={(e) => setF({ ...f, buyer_name: e.target.value })} /></Field>
        <Field label="Seller"><Input value={f.seller_name || ""} onChange={(e) => setF({ ...f, seller_name: e.target.value })} /></Field>
        <Field label="Purchase price"><Input type="number" value={f.purchase_price || ""} onChange={(e) => setF({ ...f, purchase_price: Number(e.target.value) })} data-testid="txn-price" /></Field>
        <Field label="Earnest money"><Input type="number" value={f.earnest_money || ""} onChange={(e) => setF({ ...f, earnest_money: Number(e.target.value) })} /></Field>
        <Field label="Status"><Select options={STATUSES} value={f.status} onChange={(v) => setF({ ...f, status: v })} /></Field>
        <Field label="Closing date"><Input type="date" value={f.closing_date || ""} onChange={(e) => setF({ ...f, closing_date: e.target.value })} /></Field>
        <Field label="Title company"><Input value={f.title_company || ""} onChange={(e) => setF({ ...f, title_company: e.target.value })} /></Field>
        <Field label="Contract date"><Input type="date" value={f.contract_date || ""} onChange={(e) => setF({ ...f, contract_date: e.target.value })} /></Field>
      </div>
    </FormModal>
  );
}

function TransactionDetail({ txn, onClose }) {
  const [t, setT] = useState(txn);
  const toggle = async (i) => {
    const cl = [...(t.checklist || [])];
    cl[i] = { ...cl[i], status: cl[i].status === "done" ? "pending" : "done", completed_at: new Date().toISOString() };
    setT({ ...t, checklist: cl });
    await api.put(`/transactions/${t.id}`, { checklist: cl });
  };
  const setStatus = async (v) => { setT({ ...t, status: v }); await api.put(`/transactions/${t.id}`, { status: v }); toast.success("Status updated"); };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="transaction-detail">
        <DialogHeader><DialogTitle className="font-display text-navy-800">{t.property_address}</DialogTitle></DialogHeader>
        <RecordMeta doc={t} testid="txn-detail-meta" />
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["Buyer", t.buyer_name], ["Seller", t.seller_name], ["Price", money(t.purchase_price)], ["Earnest", money(t.earnest_money)], ["Title", t.title_company || "—"], ["Closing", t.closing_date || "—"]].map(([k, v]) => (
              <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="font-medium text-navy-800">{v}</p></div>
            ))}
          </div>
          <Field label="Status"><Select options={STATUSES} value={t.status} onChange={setStatus} testid="txn-status-select" /></Field>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gold-dark mb-2">Milestone Checklist</p>
            <div className="space-y-1.5">
              {(t.checklist || []).map((c, i) => (
                <button key={i} onClick={() => toggle(i)} data-testid={`checklist-item-${i}`} className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:border-gold/40 text-left">
                  {c.status === "done" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
                  <span className={`text-sm flex-1 ${c.status === "done" ? "line-through text-slate-400" : "text-navy-800"}`}>{c.item}</span>
                  {c.due && <span className="text-xs text-slate-400">{c.due}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
